import { useStore } from 'hooks/useStore';
import { AddOpportunityPayload, AddOpportunityResponse } from 'Interfaces/BDPortalInterfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';

const AddOpportunityLogic = (_boardType: string, closeModal: () => void) => {
  const {
    UserStore: { userModulesRoles },
    BDPortalStore: {
      dropdownDetails,
      board,
      handleFetchAllConceptDetailsForSearch,
      fetchDropdownBoardDetails,
    },
  } = useStore();

  const [newOpportunity, setNewOpportunity] = useState<AddOpportunityPayload>({
    iteration_type: '',
    molecule_and_form: '',
    source_id: 0,
    stage_id: 1,
    status_id: 1,
  });
  const [addOppLoading, setAddOppLoading] = useState<boolean>(false);
  const [addOpportunityResponse, setAddOpportunityResponse] =
    useState<AddOpportunityResponse | null>(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');

  // A reference to store the latest AbortController instance
  const abortControllerRef = useRef<AbortController | null>(null);
  const [opportuntiyAlreadyExists, setOpportunityAlreadyExists] = useState(false);
  const [checkOpportunityExistsLoading, setCheckOpportuityExistsLoading] = useState(false);
  const [
    isParallelIterationModalOpenForIterationCreation,
    setIsParalleIterationModalOpenIterationCreation,
  ] = useState(false);
  const [
    isParallelIterationModalOpenForGoToOpportunity,
    setIsParalleIterationModalOpenForGoToOpportunity,
  ] = useState(false);

  interface Option {
    label: string;
    value: string;
  }
  const fetchSuggestionsWithDelay = async (inputValue: string): Promise<Option[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the previous request
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const response = await bdportalScreenServices.getMoleculeSuggestions(
            inputValue,
            controller.signal
          );
          const suggestions: Option[] = [];
          response.forEach(item => {
            suggestions.push({
              label: item.MoleculeAndForm,
              value: item.MoleculeAndForm,
            });
          });
          resolve(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          resolve([]); // Resolve with an empty array in case of an error
        }
      }, 500); // Delay of 500ms
    });
  };

  // Cleanup on component unmount (cancel any ongoing request)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    handleFetchAllConceptDetailsForSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTask = async () => {
    setAddOppLoading(true);
    try {
      const payload = {
        ...newOpportunity,
        iteration_type: board,
        molecule_and_form: searchTerm,
      };

      // check if source for the new opportuity is not 0
      if (payload.source_id === 0) {
        return toast.error('Please select a source for the new opportunity');
      }

      const res = await bdportalScreenServices.addOpportunity(payload);

      // this needs to changed later
      if (res.message.toLocaleLowerCase().includes('already exists')) {
        setAddOpportunityResponse(res);
      } else {
        setNewOpportunity({
          iteration_type: '',
          molecule_and_form: '',
          source_id: 1,
          stage_id: 1,
          status_id: 1,
        });
        handleFetchAllConceptDetailsForSearch();
        fetchDropdownBoardDetails();
        if (res?.new_opportunity_details) {
          navigate(
            `/bdportal/optracker/board?opportunityIdParam=${res?.new_opportunity_details.OpportunityID}&opportunityIterationIdParam=${res?.new_opportunity_details.IterationID}`
          );
        }
        closeModal();
      }
    } catch (_err) {
      toast.error('Failed to create opportunity, please verify Molecule Name entered is valid');
    } finally {
      setAddOppLoading(false);
    }
  };

  const handleGoToOpportunity = () => {
    if (addOpportunityResponse?.old_opportunity_details) {
      navigate(
        `/bdportal/optracker/board?opportunityIdParam=${addOpportunityResponse?.old_opportunity_details.OpportunityID.OpportunityID}&opportunityIterationIdParam=${addOpportunityResponse?.old_opportunity_details.OpportunityIterationsID}`
      );
      closeModal();
    } else {
      toast.error('Some error occured, while redirecting, please try again later');
    }
  };

  const handleClickCreateNewIteration = () => {
    if (newOpportunity.source_id === 0) {
      return toast.error('Please select a source for the new opportunity');
    }
    setIsParalleIterationModalOpenIterationCreation(true);
  };

  const handleCLickGoToIteration = () => {
    setIsParalleIterationModalOpenForGoToOpportunity(true);
  };

  const handleCheckIfOpportunityAlreadyExists = useCallback(async (searchTermValue: string, signal: AbortSignal) => {
    try {
      setCheckOpportuityExistsLoading(true);
      const data = await bdportalScreenServices.checkOpportunityAlreadyExistsForMoleculeOrNot({
        payload: { molecule_and_form: searchTermValue },
        signal,
      });
      setOpportunityAlreadyExists(data.opportunityExists);
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        toast.error('Error occured while checking opportunity already exists');
      }
    } finally {
      setCheckOpportuityExistsLoading(false);
    }
  }, []);

  const toggleParallelModalvisibilityForIterationCreation = () => {
    setIsParalleIterationModalOpenIterationCreation(
      !isParallelIterationModalOpenForIterationCreation
    );
    closeModal();
  };

  const toggleParallelModalvisibilityForGoToIteration = () => {
    setIsParalleIterationModalOpenForGoToOpportunity(
      !isParallelIterationModalOpenForGoToOpportunity
    );
    closeModal();
  };

  useEffect(() => {
    if (searchTerm) {
      const controller = new AbortController();
      const signal = controller.signal;
      handleCheckIfOpportunityAlreadyExists(searchTerm, signal);
      return () => {
        controller.abort();
      };
    }
  }, [searchTerm, handleCheckIfOpportunityAlreadyExists]);

  return {
    searchTerm,
    fetchSuggestionsWithDelay,
    setSearchTerm,
    dropdownDetails,
    userModulesRoles,
    newOpportunity,
    setNewOpportunity,
    addOppLoading,
    addTask,
    addOpportunityResponse,
    setAddOpportunityResponse,
    handleCLickGoToIteration,
    handleClickCreateNewIteration,
    handleGoToOpportunity,
    checkOpportunityExistsLoading,
    opportuntiyAlreadyExists,
    toggleParallelModalvisibilityForGoToIteration,
    toggleParallelModalvisibilityForIterationCreation,
    isParallelIterationModalOpenForIterationCreation,
    isParallelIterationModalOpenForGoToOpportunity,
  };
};

export default AddOpportunityLogic;
